/**
 * Magic-Diff-Modal: zeigt Original-Text + KI-Strukturierung Side-by-Side.
 * User kann Übernehmen/Verwerfen/Bearbeiten.
 *
 * Backend liefert:
 * - structured: { summary, key_points[], next_steps[], sentiment }
 *
 * Wird in Capture.tsx aus dem Magic-Button geöffnet (Voice→Magic-Roundtrip).
 */
import { useState } from 'react';
import { Sparkles, Check, X, Edit3 } from 'lucide-react';

export interface MagicResult {
  summary?: string;
  key_points?: string[];
  next_steps?: string[];
  sentiment?: string;
}

interface Props {
  rawText: string;
  result: MagicResult;
  onAccept: (formattedText: string) => void;
  onReject: () => void;
  onEdit?: () => void;
}

function formatStructured(r: MagicResult): string {
  // Sprint 9 · S9.4 · Sentiment NICHT im body. Wird nur als status-pill angezeigt.
  const parts: string[] = [];
  if (r.summary) parts.push(r.summary);
  if (r.key_points && r.key_points.length > 0) {
    parts.push('');
    parts.push('Bullets:');
    r.key_points.forEach((p) => parts.push(`• ${p}`));
  }
  if (r.next_steps && r.next_steps.length > 0) {
    parts.push('');
    parts.push('Nächste Schritte:');
    r.next_steps.forEach((s) => parts.push(`→ ${s}`));
  }
  return parts.join('\n');
}

export function MagicDiffModal({ rawText, result, onAccept, onReject, onEdit }: Props) {
  const [editedText, setEditedText] = useState<string | null>(null);
  const formatted = editedText ?? formatStructured(result);

  return (
    <div className="modal-overlay" onClick={onReject}>
      <div
        className="modal-card magic-diff-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Magic-Strukturierung"
      >
        <div className="magic-diff-header">
          <Sparkles size={20} strokeWidth={2} />
          <h2 className="app-shell__card-title" style={{ margin: 0 }}>
            KI-Strukturierung
          </h2>
        </div>

        <p className="form-field__hint">
          Vergleiche dein Original mit der KI-Version. Übernehmen, verwerfen oder bearbeiten.
        </p>

        <div className="magic-diff-grid">
          <div className="magic-diff-pane">
            <div className="magic-diff-pane__label">ORIGINAL</div>
            <pre className="magic-diff-pane__text">{rawText || '— leer —'}</pre>
          </div>
          <div className="magic-diff-pane magic-diff-pane--right">
            <div className="magic-diff-pane__label">KI-VERSION</div>
            {editedText !== null ? (
              <textarea
                className="auth-input form-field__textarea magic-diff-pane__edit"
                rows={10}
                value={editedText}
                onChange={(e) => setEditedText(e.target.value)}
                autoFocus
              />
            ) : (
              <pre className="magic-diff-pane__text">{formatted}</pre>
            )}
          </div>
        </div>

        {result.sentiment ? (
          <div className="magic-diff-meta">
            <span className={`status-pill status-pill--${result.sentiment === 'positiv' ? 'versendet' : result.sentiment === 'ablehnend' ? 'verworfen' : 'neu'}`}>
              {result.sentiment.toUpperCase()}
            </span>
          </div>
        ) : null}

        <div className="magic-diff-actions">
          <button
            type="button"
            className="auth-submit magic-diff-btn--accept"
            onClick={() => onAccept(formatted)}
          >
            <Check size={16} strokeWidth={2.5} /> ÜBERNEHMEN
          </button>
          <button
            type="button"
            className="app-shell__logout magic-diff-btn--edit"
            onClick={() => {
              setEditedText(formatted);
              onEdit?.();
            }}
          >
            <Edit3 size={16} strokeWidth={2.5} /> BEARBEITEN
          </button>
          <button
            type="button"
            className="app-shell__logout magic-diff-btn--reject"
            onClick={onReject}
          >
            <X size={16} strokeWidth={2.5} /> VERWERFEN
          </button>
        </div>
      </div>
    </div>
  );
}
